import type { EndpointDef } from '@skynet/data-helpers';

/**
 * Central endpoint definitions — single source of truth for the SMM (assistant-smm) API.
 *
 * This file drives:
 * - **Enhanced API**: computed endpoint keys in `enhanceEndpoints`
 * - **Hook re-exports**: clean name → generated name mapping
 * - **MSW mock factories**: HTTP method + URL path pattern
 *
 * When the OpenAPI spec changes and codegen regenerates, update this
 * file to keep everything in sync.
 */

/**
 * Endpoint definitions keyed by clean export name.
 *
 * Each entry maps a human-friendly name to:
 * - `generated` — the codegen-produced RTK Query endpoint name, derived from the OpenAPI operationId
 * - `method` — HTTP verb
 * - `path` — URL pattern (MSW-compatible `:param` syntax)
 */
export const SMM_API_ENDPOINTS = {
  listSocialPosts: {
    generated: 'socialPostsAdminControllerListSocialPostsV1',
    method: 'get',
    path: '/api/v1/admin/social-posts',
  },
  getSocialPost: {
    generated: 'socialPostsAdminControllerGetSocialPostV1',
    method: 'get',
    path: '/api/v1/admin/social-posts/:id',
  },
  createSocialPost: {
    generated: 'socialPostsAdminControllerCreateSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts',
  },
  updateSocialPost: {
    generated: 'socialPostsAdminControllerUpdateSocialPostV1',
    method: 'put',
    path: '/api/v1/admin/social-posts/:id',
  },
  deleteSocialPost: {
    generated: 'socialPostsAdminControllerDeleteSocialPostV1',
    method: 'delete',
    path: '/api/v1/admin/social-posts/:id',
  },
  regenerateSocialPost: {
    generated: 'socialPostsAdminControllerRegenerateSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/regenerate',
  },
  approveSocialPost: {
    generated: 'socialPostsAdminControllerApproveSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/approve',
  },
  unscheduleSocialPost: {
    generated: 'socialPostsAdminControllerUnscheduleSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/unschedule',
  },
  redraftSocialPost: {
    generated: 'socialPostsAdminControllerRedraftSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/redraft',
  },
  cancelSocialPost: {
    generated: 'socialPostsAdminControllerCancelSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/cancel',
  },
  unpublishSocialPost: {
    generated: 'socialPostsAdminControllerUnpublishSocialPostV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/unpublish',
  },
  presignSocialPostMedia: {
    generated: 'socialPostsAdminControllerPresignSocialPostMediaV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/media/presign',
  },
  registerSocialPostMedia: {
    generated: 'socialPostsAdminControllerRegisterSocialPostMediaV1',
    method: 'post',
    path: '/api/v1/admin/social-posts/:id/media',
  },
  removeSocialPostMedia: {
    generated: 'socialPostsAdminControllerRemoveSocialPostMediaV1',
    method: 'delete',
    path: '/api/v1/admin/social-posts/:id/media/:mediaId',
  },
  listSocialAccounts: {
    generated: 'socialAccountsAdminControllerListSocialAccountsV1',
    method: 'get',
    path: '/api/v1/admin/social-accounts',
  },
} as const satisfies Record<string, EndpointDef>;

export type SmmEndpointName = keyof typeof SMM_API_ENDPOINTS;
